import { z } from 'zod';

import type { ColumnDef, RecordRow } from '../schemas/bulk-migration';

/**
 * A record's cells, from the module's own column list (`migrator.columns()`).
 * The values are the migrator's raw values; this only decides how each one
 * reads.
 */

const MAX_CELL = 120;

export function formatCell(value: unknown): string {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toLocaleString('en-US');
    if (typeof value === 'string') return truncate(value);
    return truncate(JSON.stringify(value));
}

function truncate(text: string): string {
    return text.length > MAX_CELL ? `${text.slice(0, MAX_CELL - 1)}…` : text;
}

export interface CellView {
    readonly key: string;
    readonly label: string;
    readonly align: 'left' | 'center' | 'right';
    readonly text: string;
}

export function recordCells(
    columns: readonly ColumnDef[],
    record: Pick<RecordRow, 'columns'>,
): CellView[] {
    return columns.map((column) => ({
        key: column.key,
        label: column.label,
        align: column.align ?? 'left',
        text: formatCell(record.columns[column.key]),
    }));
}

export function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${String(bytes)} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The Branding preview — the runner's `LogoProbe`: `{ reason, format, width,
 * height, sizeBytes, detail }`, each value `null` when the probe could not
 * tell (an SVG has no pixel size; a refused download has no format). Opaque
 * to every other module. Anything that does not read as that shape renders as
 * nothing rather than as a guess.
 */
const brandingPreview = z.object({
    format: z.string().nullish(),
    width: z.number().nullish(),
    height: z.number().nullish(),
    sizeBytes: z.number().nullish(),
    reason: z.string().nullish(),
    detail: z.string().nullish(),
});

export function previewSummary(preview: unknown): string | null {
    const parsed = brandingPreview.safeParse(preview);
    if (!parsed.success) return null;
    const { format, width, height, sizeBytes, reason, detail } = parsed.data;
    const parts = [
        format?.toUpperCase(),
        typeof width === 'number' && typeof height === 'number'
            ? `${String(width)}×${String(height)}`
            : undefined,
        typeof sizeBytes === 'number' ? formatBytes(sizeBytes) : undefined,
        // `ok` is the runner saying there is nothing to say; otherwise its
        // sentence, else its code, is why the logo was refused.
        reason === 'ok' ? undefined : (detail ?? reason ?? undefined),
    ].filter((part): part is string => part !== undefined && part !== null && part.length > 0);
    return parts.length > 0 ? parts.join(' · ') : null;
}
